/**
 * Currency Context - Global currency state management for Swoopy
 * Supports IDR, USD, EUR, GBP with IDR as default
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Currency configurations
export const CURRENCIES = {
  IDR: {
    code: 'IDR',
    symbol: 'Rp',
    name: 'Indonesian Rupiah',
    flag: '🇮🇩',
    locale: 'id-ID',
    rate: 1, // Base currency
    decimals: 0
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    flag: '🇺🇸',
    locale: 'en-US',
    rate: 0.000063, // 1 IDR = 0.000063 USD (approx 15,800 IDR = 1 USD)
    decimals: 2
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    flag: '🇪🇺',
    locale: 'de-DE',
    rate: 0.000058, // 1 IDR = 0.000058 EUR (approx 17,200 IDR = 1 EUR)
    decimals: 2
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    flag: '🇬🇧',
    locale: 'en-GB',
    rate: 0.000050, // 1 IDR = 0.000050 GBP (approx 20,000 IDR = 1 GBP)
    decimals: 2
  }
};

// Travel budget presets in IDR
export const BUDGET_PRESETS_IDR = [
  { label: 'Rp5jt', value: 5000000 },
  { label: 'Rp10jt', value: 10000000 },
  { label: 'Rp15jt', value: 15000000 },
  { label: 'Rp20jt', value: 20000000 },
];

// Pricing in IDR (base currency for Midtrans)
export const PRICING_IDR = {
  basic: 49000,
  plus: 99000
};

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState('IDR');

  // Load saved currency from localStorage on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('swoopy_currency');
    if (savedCurrency && CURRENCIES[savedCurrency]) {
      setCurrencyState(savedCurrency);
    }
  }, []);

  // Save currency to localStorage when changed
  const setCurrency = useCallback((newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrencyState(newCurrency);
      localStorage.setItem('swoopy_currency', newCurrency);
    }
  }, []);

  // Format price in current currency
  const formatPrice = useCallback((amountInIDR, options = {}) => {
    const curr = CURRENCIES[currency];
    const convertedAmount = amountInIDR * curr.rate;
    
    // Use compact notation for large numbers in IDR
    const useCompact = options.compact && currency === 'IDR' && amountInIDR >= 1000000;
    
    if (useCompact) {
      const millions = amountInIDR / 1000000;
      return `Rp${millions}jt`;
    }
    
    try {
      return new Intl.NumberFormat(curr.locale, {
        style: 'currency',
        currency: curr.code,
        maximumFractionDigits: curr.decimals,
        minimumFractionDigits: curr.decimals
      }).format(convertedAmount);
    } catch (e) {
      // Fallback formatting
      return `${curr.symbol}${convertedAmount.toLocaleString(curr.locale)}`;
    }
  }, [currency]);

  // Format price with symbol only (no currency code)
  const formatPriceShort = useCallback((amountInIDR) => {
    const curr = CURRENCIES[currency];
    const convertedAmount = amountInIDR * curr.rate;
    
    if (currency === 'IDR') {
      return `Rp${Math.round(convertedAmount).toLocaleString('id-ID')}`;
    }
    
    return `${curr.symbol}${convertedAmount.toFixed(curr.decimals)}`;
  }, [currency]);

  // Convert IDR amount to current currency (for display)
  const convertFromIDR = useCallback((amountInIDR) => {
    const curr = CURRENCIES[currency];
    return amountInIDR * curr.rate;
  }, [currency]);

  // Convert current currency amount to IDR (for API calls)
  const convertToIDR = useCallback((amount) => {
    const curr = CURRENCIES[currency];
    return Math.round(amount / curr.rate);
  }, [currency]);

  // Get budget presets in current currency
  const getBudgetPresets = useCallback(() => {
    return BUDGET_PRESETS_IDR.map(preset => ({
      valueIDR: preset.value,
      valueDisplay: convertFromIDR(preset.value),
      label: formatPrice(preset.value, { compact: true })
    }));
  }, [formatPrice, convertFromIDR]);

  // Get current currency info
  const currencyInfo = CURRENCIES[currency];

  const value = {
    currency,
    setCurrency,
    currencyInfo,
    currencies: CURRENCIES,
    formatPrice,
    formatPriceShort,
    convertFromIDR,
    convertToIDR,
    getBudgetPresets,
    PRICING_IDR,
    BUDGET_PRESETS_IDR
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
